import classNames from 'classnames';
import type { HTMLAttributes, JSX } from 'react';

import type { PrimaryColor } from './types';

import styles from './ColorThemeProvider.module.scss';
import primaryStyles from '../../styles/partials/block.primary-color.module.scss';

interface Props extends HTMLAttributes<HTMLOrSVGElement> {
    /**
     * When not set, the component will render a `div` element with `display: contents` styling.
     */
    as?: keyof JSX.IntrinsicElements;
    children: React.ReactNode;
    primary: PrimaryColor;
    /**
     * When set, the container will set the background color and text color according to selected `primary` color.
     */
    isBlock?: boolean;
}

function getPrimaryColorClassName(color: PrimaryColor) {
    switch (color) {
        case 'white':
            return primaryStyles.white;
        case 'blue':
            return primaryStyles.blue;
        case 'dark_blue':
            return primaryStyles.dark_blue;
        case 'light_blue':
            return primaryStyles.light_blue;
        case 'gray':
            return primaryStyles.gray;
        case 'dark_gray':
            return primaryStyles.dark_gray;
        case 'yellow':
            return primaryStyles.yellow;
        default:
            return undefined;
    }
}

/**
 * This component doesn't provide any actual React context, just setting CSS variables that other components can use to define their color palette
 */
export function ColorThemeProvider({
    primary = 'white',
    children,
    as,
    isBlock,
    ...containerProps
}: Props) {
    const WrapperTag = as ?? 'div';

    return (
        <WrapperTag
            {...containerProps}
            className={classNames(
                getPrimaryColorClassName(primary),
                containerProps.className,
                // Only apply `display: contents` styling if we're not using a custom wrapper tag
                { [styles.container]: !as, [primaryStyles.block]: isBlock },
            )}
        >
            {children}
        </WrapperTag>
    );
}
